import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { KYBProgressStatusEnum, KYBProgressStatusHelpers } from 'anchor-kyb-service/types';
import { Typography, Button, useDevice, Column } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import { getTestIds } from 'seagull/utils/TestIds';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { openInNewTabProps } from '~/constants';
import { useStore } from '~/contexts';
import { usePersonaKYBStatus } from '~/hooks/reactQuery/queries/usePersonaKYBStatus';
import { useConnectBankAccount } from '~/hooks/useConnectBankAccount';
import { useStartKYC } from '~/hooks/useStartKYCPersona';
import { useLocaleKeys } from '~/i18n';
import { isBankAccountConnected } from '~/store/business-types';
import { selectors } from '~/store/state-selectors';
import { helpArticles } from '~/utils/helpArticles';
import { InlineBrandSetup } from './InlineBrandSetup/InlineBrandSetup';
import styles from './StepsData.module.scss';
export const GetSetupStepsTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            SetupStepsTID: {
                BrandSetup: 'setup-widget-brand',
                ConnectAccountReadMore: 'onboarding-widget-connect-account-read-more',
                ConnectAccountButton: 'onboarding-widget-connect-account-btn',
                RoutingNumber: 'onboarding-widget-routing-number',
                CompleteKyc: 'onboarding-widget-complete-kyc',
                ActivateAccountButton: 'activate-your-account-button'
            }
        }
    });
export function useSetupSteps() {
    const { isMobileDevice } = useDevice();
    const { SetupStepsTID } = useMemo(()=>GetSetupStepsTID(), []);
    const contentTextStyle = useMemo(()=>({
            color: colors.dark800
        }), []);
    const { t: setupWidgetTexts } = useLocaleKeys('home.dashboard.setupWidget');
    const worksAt = useStore(selectors.user.business);
    const payoutBankAccount = useStore(selectors.user.payoutBankAccount);
    const hasDisabledPayments = useStore(selectors.user.hasDisabledPayments);
    const { personaKYBStatus: personaKYBStatusData, isLoading: isKybLoading, error: kybError } = usePersonaKYBStatus();
    const personaKYBStatus = personaKYBStatusData === null || personaKYBStatusData === void 0 ? void 0 : personaKYBStatusData.status;
    const isKycStepComplete = personaKYBStatus && KYBProgressStatusHelpers.isApproved(personaKYBStatus);
    const { Description, ConnectButton, BankAccountDetails } = useConnectBankAccount();
    const { startKYC } = useStartKYC();
    const logoUrl = useStore(selectors.user.businessLogo);
    const numSample = useStore(selectors.user.numSampleAgreements);
    const totalSampleAgreements = Object.values(numSample !== null && numSample !== void 0 ? numSample : {}).reduce((acc, curr)=>acc + curr, 0);
    const hasSentASampleProposal = useStore(selectors.hasSentASampleProposal);
    const hasSentARealProposal = useStore(selectors.hasSentAProposal);
    const hasSentAProposal = totalSampleAgreements > 0 || hasSentASampleProposal || hasSentARealProposal;
    const isConnectBankAccountStepComplete = useStore(selectors.user.hasBankAccountConnected);
    const waitingForDataToBeLoaded = isKybLoading || !!kybError;
    const customizeBrandingStep = useMemo(()=>({
            title: setupWidgetTexts.customizeBranding.title(),
            status: logoUrl ? 'completed' : 'initial',
            content: worksAt ? /*#__PURE__*/ _jsxs(Column, {
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        style: contentTextStyle,
                        children: setupWidgetTexts.customizeBranding.content()
                    }),
                    /*#__PURE__*/ _jsx(InlineBrandSetup, {
                        testId: SetupStepsTID.BrandSetup(),
                        worksAt: worksAt
                    })
                ]
            }) : undefined
        }), [
        logoUrl,
        contentTextStyle,
        worksAt,
        SetupStepsTID
    ]);
    const sendAProposalStep = useMemo(()=>{
        return {
            title: setupWidgetTexts.sendAProposal.title(),
            status: hasSentAProposal ? 'completed' : 'initial',
            content: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        style: contentTextStyle,
                        children: setupWidgetTexts.sendAProposal.content()
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.actionButton,
                        children: /*#__PURE__*/ _jsx(CreateProposalButton, {
                            hideTemplatePicker: true,
                            text: hasSentAProposal ? setupWidgetTexts.sendAProposal.createAnotherProposalButton() : setupWidgetTexts.sendAProposal.createProposalButton()
                        })
                    })
                ]
            })
        };
    }, [
        hasSentAProposal,
        contentTextStyle
    ]);
    const connectYourBankStep = useMemo(()=>({
            title: setupWidgetTexts.connectYourBank.title(),
            status: isConnectBankAccountStepComplete ? 'completed' : 'initial',
            content: isConnectBankAccountStepComplete ? isBankAccountConnected(payoutBankAccount) && /*#__PURE__*/ _jsx(BankAccountDetails, {
                payoutBankAccount: payoutBankAccount,
                style: contentTextStyle
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Description, {
                        style: contentTextStyle,
                        linkTestId: SetupStepsTID.ConnectAccountReadMore()
                    }),
                    /*#__PURE__*/ _jsx(ConnectButton, {
                        className: styles.actionButton,
                        testId: SetupStepsTID.ConnectAccountButton(),
                        enterManuallyTestId: SetupStepsTID.RoutingNumber()
                    })
                ]
            })
        }), [
        isConnectBankAccountStepComplete,
        payoutBankAccount,
        Description,
        ConnectButton,
        BankAccountDetails,
        contentTextStyle,
        SetupStepsTID
    ]);
    const verifyYourBusinessStep = useMemo(()=>{
        const title = setupWidgetTexts.verifyYourBusiness.title();
        const subtextInfo = /*#__PURE__*/ _jsxs(Typography, {
            style: contentTextStyle,
            variant: "paragraph2",
            className: styles.kybNote,
            children: [
                setupWidgetTexts.verifyYourBusiness.subtextInfo(),
                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                    variant: "paragraph2",
                    openInNewTab: true,
                    underline: "none",
                    href: helpArticles.kybProcessOverview(),
                    bi: {
                        name: 'kyb_process_read_more'
                    },
                    children: setupWidgetTexts.verifyYourBusiness.readMoreLink()
                })
            ]
        });
        if (isKycStepComplete) {
            return {
                title,
                status: 'completed',
                content: /*#__PURE__*/ _jsx(Typography, {
                    style: contentTextStyle,
                    children: setupWidgetTexts.verifyYourBusiness.completedContent()
                })
            };
        }
        if (personaKYBStatus && KYBProgressStatusHelpers.isAwaitingDecision(personaKYBStatus) || personaKYBStatus === KYBProgressStatusEnum.Errored) {
            return {
                title,
                status: 'pending',
                content: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            style: contentTextStyle,
                            children: setupWidgetTexts.verifyYourBusiness.pendingContent()
                        }),
                        subtextInfo
                    ]
                })
            };
        }
        return {
            title,
            status: 'initial',
            content: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        style: contentTextStyle,
                        children: setupWidgetTexts.verifyYourBusiness.initialContent()
                    }),
                    subtextInfo,
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.actionButton,
                        children: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'onboarding_complete_kyc'
                            },
                            testId: SetupStepsTID.CompleteKyc(),
                            onClick: startKYC,
                            children: setupWidgetTexts.verifyYourBusiness.completeKybButton()
                        })
                    })
                ]
            })
        };
    }, [
        isKycStepComplete,
        personaKYBStatus,
        startKYC,
        contentTextStyle,
        SetupStepsTID
    ]);
    const activateYourAccountStep = useMemo(()=>{
        const disabled = !isKycStepComplete || !isConnectBankAccountStepComplete;
        return {
            title: setupWidgetTexts.activateYourAccount.title(),
            status: hasDisabledPayments ? 'initial' : 'completed',
            content: hasDisabledPayments ? /*#__PURE__*/ _jsxs(Column, {
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        style: contentTextStyle,
                        children: setupWidgetTexts.activateYourAccount.content()
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        children: /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({
                            bi: {
                                name: 'activate_account_schedule_call'
                            },
                            size: "medium",
                            testId: SetupStepsTID.ActivateAccountButton(),
                            as: "a",
                            href: "https://book.avoma.com/anchor/enable-payments/"
                        }, openInNewTabProps), {
                            tooltip: disabled ? {
                                title: /*#__PURE__*/ _jsx("div", {
                                    children: setupWidgetTexts.activateYourAccount.tooltip()
                                })
                            } : undefined,
                            disabled: disabled,
                            children: setupWidgetTexts.activateYourAccount.verifyIdentityButton()
                        }))
                    }),
                    isMobileDevice && /*#__PURE__*/ _jsx(Typography, {
                        skin: "secondary",
                        children: setupWidgetTexts.activateYourAccount.tooltip()
                    })
                ]
            }) : /*#__PURE__*/ _jsx(Typography, {
                style: contentTextStyle,
                children: setupWidgetTexts.activateYourAccount.completedContent()
            })
        };
    }, [
        hasDisabledPayments,
        isConnectBankAccountStepComplete,
        isKycStepComplete,
        contentTextStyle,
        isMobileDevice,
        SetupStepsTID
    ]);
    return useMemo(()=>({
            steps: waitingForDataToBeLoaded ? [] : [
                customizeBrandingStep,
                sendAProposalStep,
                connectYourBankStep,
                verifyYourBusinessStep,
                activateYourAccountStep
            ],
            isLoading: waitingForDataToBeLoaded
        }), [
        customizeBrandingStep,
        sendAProposalStep,
        connectYourBankStep,
        verifyYourBusinessStep,
        activateYourAccountStep,
        waitingForDataToBeLoaded
    ]);
}
